import { useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { checkAuth, getLogoutPath, validateMenuPathForEmployee } from "../../utils/common";
import { ValidateReq,ValidateRes } from "../../utils/types";
import { whenServiceOk } from "../../global/serviceStatus";
const useMenuPathValidation = (actualPath: string, excludedPaths: string[] = []) => {
  const navigate = useNavigate();
  // the page the user is on now: an answer for a page already left is ignored, otherwise a quick move
  // (a list opening its first record) sent the user to /404
  const current = useRef(actualPath);
  current.current = actualPath;
  // re-checks of a page after the server was unavailable (a few, then the API's own checks on each call apply)
  const retries = useRef<Record<string, number>>({});

  const { mutate: validateMenuPath } = useMutation<
    AxiosResponse<ValidateRes>,
    Error,
    ValidateReq
  >({
    mutationKey: ["validateMenuPath"],
    mutationFn: validateMenuPathForEmployee, // Assuming this function exists
    onSuccess: (resp: any, vars: ValidateReq) => {
      if (vars.menu_path !== current.current) return true;
      delete retries.current[vars.menu_path];
      if (resp.status === true) {
        if (resp.result[0].MENU_PROG_PATH === vars.menu_path) {
          return true;
        }
      }
      navigate("/404");
      return false;
    },
    onError: (e: Error, vars: ValidateReq) => {
      // no login token (session ended or logged out in another tab): the API
      // answers 401, which is not a rights problem, so go to the login page
      if (!checkAuth()) {
        window.location.href = getLogoutPath();
        return false;
      }
      if (vars.menu_path !== current.current) return true;
      // server unavailable: not a rights answer, so no 404. The connection screen shows while it is down;
      // check the page again once it answers.
      if ((e as any)?.unavailable) {
        const n = (retries.current[vars.menu_path] ?? 0) + 1;
        retries.current[vars.menu_path] = n;
        if (n <= 3) {
          whenServiceOk(() => {
            if (vars.menu_path === current.current) setTimeout(() => validateMenuPath(vars), 1500 * n);
          });
        }
        return false;
      }
      navigate("/404");
      return false;
    },
  });

  const executeValidation = (menu_path: string,menu_right_code:string) => {
    if (excludedPaths.includes(menu_path)) {
      return; // Skip validation for excluded paths
    }
    validateMenuPath({ menu_path,menu_right_code });
  };

  return executeValidation;
};

export default useMenuPathValidation;
